import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import AppLayout from "../../components/AppLayout";
import StatusBadge from "../../components/StatusBadge";
import ParentWelcomeDialog from "../../components/ParentWelcomeDialog";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../lib/AuthContext";
import api, { formatCurrency, formatDate, daysUntil, formatMethod } from "../../lib/api";
import { withReturnTo } from "../../lib/backNavigation";
import { recordDashboardVisit } from "../../lib/installMilestone";

export default function ParentDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  // `loading` distinguishes "not fetched yet" from "fetched and genuinely
  // empty." Without this, `data?.totals || {}` yields `owedNum = 0` and
  // renders the confident "Nothing due" state during the initial paint —
  // the P0 UX bug where parents saw "Nothing due" on Home while an
  // unpaid fee was visible on Pay. See PRD for the full RCA.
  const [loading, setLoading] = useState(true);
  // `fetchError` is set only when a background refetch failed. Prior code
  // wiped `data` to an empty shell on any error — a transient mobile
  // fetch failure would erase the "Recently confirmed" section the
  // parent had just seen. We now keep the last-known-good snapshot and
  // surface a subtle retry affordance instead.
  const [fetchError, setFetchError] = useState(false);
  const visitRecorded = useRef(false);

  const load = useCallback(() => {
    setLoading(true);
    return api.get("/parent/dashboard")
      .then((r) => { setData(r.data); setFetchError(false); })
      .catch(() => {
        // Preserve prior data. Only if we have literally nothing yet do
        // we fall back to an empty shell so the render tree doesn't
        // crash on `.records.filter(...)` etc.
        setData((prev) => prev || { totals: {}, players: [], records: [] });
        setFetchError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!visitRecorded.current) {
      visitRecorded.current = true;
      recordDashboardVisit();
    }
    load();
    // Refetch whenever the parent returns to this page. Covers:
    //   • iOS Safari back-forward cache (bfcache) restores — swiping back
    //     from /parent/owed/:id/pay to Home restores the DOM without
    //     re-running useEffect, so a stale "Nothing due" would linger
    //     even though Pay showed the correct $125.
    //   • Installed-PWA resume from the app switcher.
    //   • Tab visibility flips (parent switches to their coach's text and
    //     back). Without this, a fee assigned in the coach app while the
    //     parent's screen was idle stays invisible on the parent's Home
    //     until they force-reload.
    // The extra fetches are cheap (single un-paginated dashboard call) and
    // guarantee HOME and PAY always derive from the same authoritative
    // snapshot.
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    const onPageShow = (e) => {
      if (e.persisted) load();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [load]);

  const t = data?.totals || {};
  const records = data?.records || [];
  const open = records.filter((r) => r.status === "unpaid" || r.status === "rejected");
  const submitted = records.filter((r) => r.status === "submitted");
  const recent = records.filter((r) => r.status === "confirmed").slice(0, 3);

  const owedNum = Number(t.owed || 0);
  const allClear = open.length === 0 && submitted.length === 0;

  return (
    <AppLayout>
      {/* One-time welcome dialog — opens automatically on first visit after
          an invite is accepted; "Got it" clears the localStorage flag. */}
      <ParentWelcomeDialog />

      {/* Non-blocking notice when a background refetch failed but we still
          have a good previous snapshot. Prevents a transient mobile
          connectivity hiccup from silently erasing the parent's
          "Recently confirmed" section during nav or PWA resume. */}
      {fetchError && data ? (
        <div
          className="mb-4 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
          data-testid="parent-home-stale-notice"
        >
          <span>Showing last known amounts — couldn't refresh just now.</span>
          <button
            onClick={() => { setFetchError(false); load(); }}
            className="ml-3 font-semibold text-amber-900 underline"
            data-testid="parent-home-retry"
          >
            Retry
          </button>
        </div>
      ) : null}

      {/* Big hero — what you owe right now. While the initial fetch is in
          flight we render a neutral skeleton instead of committing to a
          "Nothing due" verdict; the skeleton also renders when a
          refetch (visibility, bfcache) is in progress so an old "Nothing
          due" render never lingers with stale data. */}
      {loading && data === null ? (
        <section
          data-testid="parent-hero-loading"
          className="rounded-2xl border border-ink-100 bg-white p-6 shadow-sm"
          aria-busy="true"
          aria-live="polite"
        >
          <p className="label-caps">Hi {user?.name?.split(" ")[0] || "there"}</p>
          <div className="mt-2 h-4 w-32 animate-pulse rounded bg-ink-100" />
          <div className="mt-3 h-10 w-40 animate-pulse rounded bg-ink-100" />
          <div className="mt-2 h-3 w-48 animate-pulse rounded bg-ink-100" />
        </section>
      ) : (
        <section
          data-testid="parent-hero"
          className={`rounded-2xl border p-6 shadow-sm ${
            owedNum > 0
              ? "border-rose-200 bg-gradient-to-br from-rose-50 to-white"
              : submitted.length > 0
              ? "border-amber-200 bg-gradient-to-br from-amber-50 to-white"
              : "border-emerald-200 bg-gradient-to-br from-emerald-50 to-white"
          }`}
        >
          <p className="label-caps">Hi {user?.name?.split(" ")[0] || "there"}</p>
          {owedNum > 0 ? (
            <>
              <p className="mt-1 text-sm font-semibold text-ink-700">You owe</p>
              <p className="mt-1 font-heading text-5xl font-bold text-rose-700" data-testid="parent-hero-amount">
                {formatCurrency(owedNum)}
              </p>
              <p className="mt-1 text-sm text-ink-500">
                {open.length} open {open.length === 1 ? "fee" : "fees"} · {t.players || 0}{" "}
                {(t.players || 0) === 1 ? "player" : "players"}
              </p>
              <Link to="/parent/owed" data-testid="parent-hero-cta">
                <Button className="mt-4 h-12 w-full bg-brand font-semibold text-white hover:bg-brand-hover sm:w-auto">
                  View Fees <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </>
          ) : submitted.length > 0 ? (
            <>
              <p className="mt-1 text-sm font-semibold text-ink-700">Waiting on coach</p>
              <p className="mt-1 font-heading text-5xl font-bold text-amber-700">
                {formatCurrency(t.submitted || 0)}
              </p>
              <p className="mt-1 text-sm text-ink-500">
                {submitted.length} {submitted.length === 1 ? "payment" : "payments"} waiting for confirmation
              </p>
            </>
          ) : (
            <>
              <p className="mt-1 text-sm font-semibold text-ink-700">You&apos;re all caught up</p>
              <p className="mt-1 font-heading text-3xl font-bold text-emerald-700">
                <CheckCircle2 className="mr-2 inline h-7 w-7" /> Nothing due
              </p>
              <p className="mt-2 text-sm text-ink-500">We&apos;ll let you know when a new fee appears.</p>
            </>
          )}
        </section>
      )}

      {/* Open fees — only show if any */}
      {open.length > 0 ? (
        <section className="mt-6">
          <h2 className="h3">Fees to Pay</h2>
          <div className="mt-3 space-y-3">
            {open.map((r) => {
              const d = daysUntil(r.charge?.due_date);
              const tone = d != null && d < 0 ? "text-rose-600" : d != null && d <= 3 ? "text-amber-600" : "text-ink-700";
              return (
                <Link
                  key={r.id}
                  to={withReturnTo(`/parent/owed/${r.id}/pay`, { from: "/parent/dashboard", label: "Home" })}
                  className="block rounded-xl border border-ink-100 bg-white p-4 active:bg-ink-100/50 sm:hover:bg-ink-100/50"
                  data-testid={`parent-open-${r.id}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-heading text-base font-bold text-ink-900">{r.charge?.title}</p>
                      <p className="truncate text-xs text-ink-500">
                        {r.player?.first_name} {r.player?.last_name} · {r.team?.name}
                      </p>
                      <p className={`mt-1 text-xs font-semibold ${tone}`}>
                        Due {formatDate(r.charge?.due_date)} {d == null ? "" : d < 0 ? `· ${Math.abs(d)}d overdue` : d === 0 ? "· today" : `· in ${d}d`}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="font-heading text-xl font-bold text-ink-900">{formatCurrency(r.amount)}</p>
                      <span className="mt-1 inline-flex items-center text-xs font-bold text-brand">
                        How to Pay <ArrowRight className="ml-0.5 h-3 w-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* Submitted — waiting on coach */}
      {submitted.length > 0 ? (
        <section className="mt-6">
          <h2 className="h3">Waiting on coach</h2>
          <div className="mt-3 space-y-2">
            {submitted.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid={`parent-submitted-${r.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-ink-900">{r.charge?.title}</p>
                  <p className="truncate text-xs text-ink-700">Submitted via {formatMethod(r.method) || "—"}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-amber-700">{formatCurrency(r.amount)}</p>
                  <StatusBadge status={r.status} />
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* Recent confirmed - show only if we have nothing more urgent */}
      {allClear && recent.length > 0 ? (
        <section className="mt-6">
          <div className="flex items-center justify-between">
            <h2 className="h3">Recently confirmed</h2>
            <Link to="/parent/history" className="text-sm font-semibold text-brand hover:underline" data-testid="parent-history-link">
              See all <ArrowRight className="ml-0.5 inline h-3 w-3" />
            </Link>
          </div>
          <div className="mt-3 space-y-2">
            {recent.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-xl border border-ink-100 bg-white p-4">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-ink-900">{r.charge?.title}</p>
                  <p className="truncate text-xs text-ink-500">{r.player?.first_name} {r.player?.last_name} · {formatMethod(r.method) || "—"}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-emerald-700">{formatCurrency(r.amount)}</p>
                  <StatusBadge status={r.status} />
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* Support hint — subtle but present so parents always have a way to reach out */}
      <p className="mt-8 text-center text-xs text-ink-500" data-testid="parent-support-hint">
        Questions about a payment? Message your coach directly, or email{" "}
        <a href="mailto:support@squadfee.com" className="font-semibold text-brand hover:underline">
          support@squadfee.com
        </a>
        .
      </p>
    </AppLayout>
  );
}
